import { useEffect, useState } from 'react';
import { getTaxonomies, type WbdTaxonomies, type WbdTerm } from './api';

// The World Buildings Directory WordPress taxonomies, copied verbatim from WBD
// (duplicates and all) so every option staff can pick on WBD is also pickable
// here, and the WBD export writes term names WP All Import already knows.
// Clean-up of the lists themselves is NOC-407.

export const TAX = {
  category: 'waf_category',
  categoryStatus: 'waf_category_status',
  prize: 'waf_prize',
  prizeStatus: 'waf_prize_status',
  projectCountry: 'project_country',
  architectCountry: 'architect_country',
} as const;

// Multi-value storage formats — must match Application.Taxonomies.MultiValue.
export const STATUS_SEPARATOR = ', ';
export const PRIZE_SEPARATOR = '|';
export const SHORTLIST = 'Shortlist';

export function splitValues(value: string | null | undefined, separator: string): string[] {
  if (!value) return [];
  const parts = value.split(separator.trim()).map((s) => s.trim()).filter(Boolean);
  return Array.from(new Set(parts));
}

export function joinValues(values: string[], separator: string): string {
  return Array.from(new Set(values.map((v) => v.trim()).filter(Boolean))).join(separator);
}

// One fetch per page load; every picker shares it.
let cache: Promise<WbdTaxonomies> | null = null;

export function useTaxonomies(): WbdTaxonomies | null {
  const [data, setData] = useState<WbdTaxonomies | null>(null);
  useEffect(() => {
    let cancelled = false;
    cache ??= getTaxonomies().catch((err) => {
      cache = null; // let the next mount retry
      throw err;
    });
    cache.then((d) => !cancelled && setData(d)).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  return data;
}

/** A picker option: the WBD term name, plus the parent's name for grouping. */
export interface TermOption {
  name: string;
  group: string;
}

function toOptions(terms: WbdTerm[]): TermOption[] {
  const bySlug = new Map(terms.map((t) => [t.slug, t]));
  const seen = new Set<string>();
  const out: TermOption[] = [];
  for (const t of terms) {
    // Some WBD terms share a name (e.g. two "WAF Future Project: Experimental");
    // the export writes names, so one option per name is all that's meaningful.
    if (seen.has(t.name)) continue;
    seen.add(t.name);
    const parent = t.parent ? bySlug.get(t.parent) : undefined;
    out.push({ name: t.name, group: parent?.name ?? '' });
  }
  return out;
}

/** Slugs of a term and everything beneath it. */
function subtree(terms: WbdTerm[], rootSlug: string): Set<string> {
  const slugs = new Set([rootSlug]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const t of terms) {
      if (t.parent && slugs.has(t.parent) && !slugs.has(t.slug)) {
        slugs.add(t.slug);
        grew = true;
      }
    }
  }
  return slugs;
}

const isWafx = (t: WbdTerm, wafxSlugs: Set<string>) => wafxSlugs.has(t.slug) || t.name.startsWith('WAFX');

export function categoryOptions(tax: WbdTaxonomies): TermOption[] {
  return toOptions(tax[TAX.category] ?? []);
}

export function statusOptions(tax: WbdTaxonomies): TermOption[] {
  return toOptions(tax[TAX.categoryStatus] ?? []);
}

/** WBD's waf_prize minus the WAFX Award subtree (that has its own picker). */
export function specialPrizeOptions(tax: WbdTaxonomies): TermOption[] {
  const terms = tax[TAX.prize] ?? [];
  const wafx = subtree(terms, 'wafx-award');
  return toOptions(terms).filter((o) => !isWafx(terms.find((t) => t.name === o.name)!, wafx));
}

/** The WAFX Award subtree of WBD's waf_prize (plus any stray top-level WAFX term). */
export function wafxOptions(tax: WbdTaxonomies): TermOption[] {
  const terms = tax[TAX.prize] ?? [];
  const wafx = subtree(terms, 'wafx-award');
  return toOptions(terms.filter((t) => isWafx(t, wafx)));
}

export function prizeStatusOptions(tax: WbdTaxonomies): TermOption[] {
  return toOptions(tax[TAX.prizeStatus] ?? []);
}

export function countryOptions(tax: WbdTaxonomies, which: 'project' | 'architect'): TermOption[] {
  return toOptions(tax[which === 'project' ? TAX.projectCountry : TAX.architectCountry] ?? []);
}

// Completion dates are stored day-first ("23/09/2015"); <input type="date">
// speaks ISO ("2015-09-23").
export function ukDateToIso(value: string | null | undefined): string {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((value ?? '').trim());
  return m ? `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}` : '';
}

export function isoDateToUk(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}
